import React, { forwardRef, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { FieldErrorText } from "@purpur/field-error-text";
import { FieldHelperText } from "@purpur/field-helper-text";
import { Icon } from "@purpur/icon";
import { checkmarkBold } from "@purpur/icon/assets/checkmark-bold";
import { minusBold } from "@purpur/icon/assets/minus-bold";
import { Label } from "@purpur/label";
import { Paragraph } from "@purpur/paragraph";
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import c from "classnames/bind";

import styles from "./checkbox.module.scss";
const cx = c.bind(styles);

export const checkedState = ["indeterminate", true, false] as const;

export type CheckedState = (typeof checkedState)[number];
export type CheckboxProps = Omit<BaseProps<"button">, "onChange" | "children"> & {
  /**
   * To use with custom label (not recommended).
   * */
  ["aria-labelledby"]?: string;
  /**
   * To use when no label is given (not recommended).
   * */
  ["aria-label"]?: string;
  checked?: CheckedState;
  /**
   * The checked state of the checkbox when it is initially rendered. Use when you do not need to control its checked state.
   */
  defaultChecked?: boolean;
  /**
   * When `true`, prevents the user from interacting with the checkbox.
   */
  disabled?: boolean;
  /**
   * Use to render error state on the actual checkbox only. No error text below the checkbox will appear and the label will not
   * be shown as an error, unless errorText is set.
   * */
  error?: boolean;
  /**
   * Use to render error message below the checkbox. The checkbox renders with error appearance.
   * */
  errorText?: string;
  /**
   * ID of the checkbox.
   * */
  id: string;
  /**
   * The label of the checkbox. Label can't be a block element
   * */
  label?: ReactNode;
  /**
   * The name of the checkbox. Submitted with its owning form as part of a name/value pair.
   * */
  name?: string;
  /**
   * The helper text displayed below the checkbox.
   */
  helperText?: string;
  /**
   * When `true`, renders the checkbox for use on dark or colored backgrounds.
   */
  negative?: boolean;
  /**
   * Event handler called when the checked state of the checkbox changes.
   * */
  onChange?: (value: CheckedState) => void;
  /**
   * When `true`, indicates that the user must check the checkbox before the owning form can be submitted.
   * */
  required?: boolean;
  /**
   * The value given as data when submitted with a name.
   * */
  value?: string;
};

const rootClassName = "purpur-checkbox";

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      ["data-testid"]: dataTestId,
      checked,
      className,
      error = false,
      errorText,
      label,
      helperText,
      negative = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const isError = errorText && !checked;
    const isCheckboxError = error && !checked;

    return (
      <div className={cx([`${rootClassName}__wrapper`, className])}>
        <div className={cx([`${rootClassName}__container`, className])}>
          <RadixCheckbox.Root
            {...props}
            checked={checked}
            className={cx([
              rootClassName,
              {
                [`${rootClassName}--error`]: isError,
                [`${rootClassName}--error-checkbox`]: isCheckboxError,
                [`${rootClassName}--negative`]: negative,
              },
            ])}
            data-testid={dataTestId}
            onCheckedChange={onChange}
            ref={ref}
          >
            <span className={cx(`${rootClassName}__box`)}>
              <RadixCheckbox.Indicator className={cx(`${rootClassName}__indicator`)}>
                {checked === "indeterminate" ? (
                  <Icon
                    data-testid={dataTestId && `${dataTestId}-checked-icon`}
                    size="xs"
                    svg={minusBold}
                  />
                ) : (
                  <Icon
                    data-testid={dataTestId && `${dataTestId}-indeterminate-icon`}
                    size="xs"
                    svg={checkmarkBold}
                  />
                )}
              </RadixCheckbox.Indicator>
            </span>
          </RadixCheckbox.Root>
          <span className={cx(`${rootClassName}__label-container`)}>
            {label && (
              <Label
                htmlFor={props.id}
                data-testid={dataTestId && `${dataTestId}-label`}
                disabled={props.disabled}
                negative={negative}
              >
                <Paragraph variant="paragraph-100" disabled={props.disabled} negative={negative}>
                  {props.required && <span aria-hidden>{"* "}</span>}
                  {label}
                </Paragraph>
              </Label>
            )}
            {helperText && (
              <FieldHelperText
                className={cx(`${rootClassName}__helper`)}
                data-testid={dataTestId && `${dataTestId}-helper-text`}
                disabled={props.disabled}
                id={`${props.id}-helper-text`}
                negative={negative}
              >
                {helperText}
              </FieldHelperText>
            )}
          </span>
        </div>
        {isError && (
          <FieldErrorText data-testid={dataTestId && `${dataTestId}-error-text`} negative={negative}>
            {errorText}
          </FieldErrorText>
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";
